import React, { useState, useEffect } from "react";
import { Shield, Loader2, Check, Users, Lock } from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import { foldersApi, usersApi } from "../../services/api";

interface TeamMember {
  _id: string;
  name: string;
  email: string;
}

interface FolderPermissionsModalProps {
  open: boolean;
  onClose: () => void;
  folderId: string;
  folderName: string;
  currentAccessLevel: "all" | "restricted";
  currentAllowedUserIds: string[];
  onUpdated: () => void;
}

const FolderPermissionsModal: React.FC<FolderPermissionsModalProps> = ({
  open,
  onClose,
  folderId,
  folderName,
  currentAccessLevel,
  currentAllowedUserIds,
  onUpdated,
}) => {
  const [accessLevel, setAccessLevel] = useState<"all" | "restricted">(currentAccessLevel);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(currentAllowedUserIds);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAccessLevel(currentAccessLevel);
      setSelectedUserIds(currentAllowedUserIds);
      setLoading(true);
      usersApi
        .list({ limit: 100 })
        .then((res) => {
          const data = (res.data as any).data || res.data || [];
          setTeamMembers(data);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, currentAccessLevel, currentAllowedUserIds]);

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await foldersApi.updatePermissions(folderId, {
        accessLevel,
        allowedUserIds: accessLevel === "restricted" ? selectedUserIds : [],
      });
      onUpdated();
      onClose();
    } catch (err) {
      console.error("Failed to update permissions:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title="Folder Access"
      subtitle={folderName}
      icon={Shield}
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (accessLevel === "restricted" && selectedUserIds.length === 0)}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </span>
            ) : (
              "Save"
            )}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Access Level Toggle */}
        <div>
          <div className="flex items-center gap-3 pt-2 mb-3">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Access Level
            </span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setAccessLevel("all")}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                accessLevel === "all"
                  ? "border-brand-accent bg-brand-light text-brand"
                  : "border-slate-200 hover:border-slate-300 text-slate-600"
              }`}
            >
              <Users className="h-5 w-5" />
              <span className="text-sm font-medium">Everyone</span>
              <span className="text-xs text-center opacity-70">All team members</span>
            </button>
            <button
              type="button"
              onClick={() => setAccessLevel("restricted")}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                accessLevel === "restricted"
                  ? "border-brand-accent bg-brand-light text-brand"
                  : "border-slate-200 hover:border-slate-300 text-slate-600"
              }`}
            >
              <Lock className="h-5 w-5" />
              <span className="text-sm font-medium">Restricted</span>
              <span className="text-xs text-center opacity-70">Selected members only</span>
            </button>
          </div>
        </div>

        {/* User Selection (only when restricted) */}
        {accessLevel === "restricted" && (
          <div>
            <div className="flex items-center gap-3 pt-2 mb-3">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Allowed Members
              </span>
              <div className="flex-1 h-px bg-slate-100" />
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {teamMembers.map((member) => (
                  <button
                    key={member._id}
                    type="button"
                    onClick={() => toggleUser(member._id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      selectedUserIds.includes(member._id)
                        ? "bg-brand-light border border-brand-light"
                        : "hover:bg-slate-50 border border-transparent"
                    }`}
                  >
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                        selectedUserIds.includes(member._id)
                          ? "bg-brand-accent text-white"
                          : "bg-slate-200 text-slate-600"
                      }`}
                    >
                      {member.name
                        .split(" ")
                        .slice(0, 2)
                        .map((w) => w[0]?.toUpperCase())
                        .join("")}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{member.name}</p>
                      <p className="text-xs text-slate-500 truncate">{member.email}</p>
                    </div>
                    {selectedUserIds.includes(member._id) && (
                      <Check className="h-4 w-4 text-brand-accent flex-shrink-0" />
                    )}
                  </button>
                ))}
                {teamMembers.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No team members found</p>
                )}
              </div>
            )}
            {selectedUserIds.length === 0 && (
              <p className="text-xs text-amber-600 mt-2">
                Select at least one team member to restrict access.
              </p>
            )}
          </div>
        )}
      </div>
    </PremiumModal>
  );
};

export default FolderPermissionsModal;
